import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ExternalLink, Globe, Users, ArrowRight, Plane } from "lucide-react";
import api from "../lib/api";
import { Spinner, Pill } from "../components/common";
import { GuideTeaser } from "../components/ArticleSections";
import { GUIDES } from "../content/guides";
import LoyaltyArticle from "../components/LoyaltyArticle";
import CategoryTiles from "../components/loyalty/CategoryTiles";
import GmailLoyaltyCard from "../components/loyalty/GmailLoyaltyCard";

const ALLIANCES = ["Star Alliance", "oneworld", "SkyTeam", "Independent"];
const TONE = { "Star Alliance": "amber", oneworld: "blue", SkyTeam: "green", Independent: "muted" };

export default function LoyaltyExplore() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/loyalty/programs").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, []);
  if (!data) return <Spinner full />;

  const grouped = ALLIANCES.map((al) => ({ alliance: al, items: data.items.filter((p) => p.alliance === al) }));

  return (
    <div data-testid="loyalty-explore">
      <section className="relative border-b border-zinc-200 bg-orange-800">
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-300 mb-3 flex items-center gap-2"><Award className="w-4 h-4" /> Airline Loyalty & Miles</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white max-w-3xl">Explore & join airline loyalty programs</h1>
          <p className="text-zinc-300 mt-4 max-w-2xl text-lg">Compare {data.count || data.items.length} major frequent-flyer programs across every global alliance — see partners, join in one click, and track all your miles in one place.</p>
          <Link to="/travel-hub" data-testid="loyalty-explore-hub-cta"
            className="inline-flex items-center gap-2 mt-6 bg-amber-500 text-white px-5 py-3 font-600 hover:bg-amber-600 transition-colors">
            <Plane className="w-5 h-5 -rotate-45" /> Manage all your Airline Miles Programs on My Travel Hub
          </Link>
          <Link to="/loyalty/catalog" data-testid="loyalty-catalog-cta"
            className="inline-flex items-center gap-2 mt-6 ml-3 bg-white text-orange-800 px-5 py-3 font-700 hover:brightness-95 transition-colors">
            Browse the full programs catalog →
          </Link>
        </div>
      </section>

      {/* Colored category tiles — deep-linked into the catalog */}
      <CategoryTiles />

      {/* Auto-detect balances from Gmail monthly statements */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-8">
        <GmailLoyaltyCard />
      </div>

      {/* Loyalty & miles guide article */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-10">
        <GuideTeaser guide={GUIDES.deals} />
        <LoyaltyArticle />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 space-y-12">
        {grouped.map(({ alliance, items }) => items.length > 0 && (
          <div key={alliance} data-testid={`loyalty-alliance-${alliance.replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-3 mb-5">
              <h2 className="font-heading font-900 text-2xl sm:text-3xl">{alliance}</h2>
              <Pill tone={TONE[alliance]}>{items.length} programs</Pill>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => <ProgramCard key={p.airline} p={p} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgramCard({ p }) {
  return (
    <div className="border border-zinc-300 bg-white p-5 flex flex-col" data-testid={`loyalty-program-${p.iata || p.airline.replace(/\s/g, "")}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-900 text-lg text-amber-600 truncate">{p.program}</div>
          <div className="text-sm text-muted-foreground truncate">{p.airline}</div>
        </div>
        <Award className="w-5 h-5 text-amber-400 shrink-0" />
      </div>

      {p.partners?.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><Users className="w-3.5 h-3.5" /> Partners</div>
          <div className="text-xs text-zinc-600 line-clamp-2">{p.partners.slice(0, 6).join(" · ")}{p.partners.length > 6 ? " …" : ""}</div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap gap-2">
        <a href={p.link} target="_blank" rel="noreferrer" data-testid={`loyalty-join-${p.iata || ""}`}
          className="inline-flex items-center gap-1.5 bg-amber-500 text-white px-3 py-2 text-xs font-600 hover:bg-amber-600 transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> Join program
        </a>
        {p.airline_link && (
          <a href={p.airline_link} target="_blank" rel="noreferrer" data-testid={`loyalty-airline-site-${p.iata || ""}`}
            className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-2 text-xs font-600 hover:border-primary transition-colors">
            <Globe className="w-3.5 h-3.5" /> Airline site
          </a>
        )}
        {p.airline_id && (
          <Link to={`/airlines/${p.airline_id}#loyalty`} data-testid={`loyalty-details-${p.iata || ""}`}
            className="inline-flex items-center gap-1.5 text-primary px-3 py-2 text-xs font-600 hover:underline ml-auto">
            Details <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}
